import React from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  TableOptions,
  useReactTable,
  Table as TanStackTable,
  Row,
} from '@tanstack/react-table';
import { H6 } from '../Typography/Typography';
import { DataTableType } from './types';
import classNames from 'classnames';

interface TableProps {
  columns: ColumnDef<DataTableType>[];
  data: DataTableType[];
  className?: string;
  rowsPerPage?: number;
  onRowClick?: (row: Row<DataTableType>) => void;
}

const Table: React.FC<TableProps> = ({ columns, data, className, rowsPerPage, onRowClick }) => {
  // We manage pagination externally via Redux, so don't paginate here
  // Just pass through all the data we receive (already paginated from server)
  const options: TableOptions<any> = {
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  };
  const table: TanStackTable<any> = useReactTable(options);

  return (
    <>
      {/* Desktop Table View */}
      <table className={classNames(className || 'min-w-full border-collapse table-fixed', 'hidden md:table')}>
        <thead>
          {table.getHeaderGroups().map((group, i) => (
            <tr key={group.id}>
              {group.headers.map(header => (
                <th key={header.id} className="px-5 py-3 border-b text-left text-gray-600">
                  <H6>{flexRender(header.column.columnDef.header, header.getContext())}</H6>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="h-auto">
          {table.getRowModel().rows.map((row, i) => (
            <tr
              onClick={e => {
                const selection = window.getSelection();
                if (onRowClick && (!selection || selection.toString().length === 0)) {
                  onRowClick(row as Row<DataTableType>);
                }
              }}
              className={classNames('table-row max-h-12', onRowClick ? 'cursor-pointer hover:bg-gray-100' : '')}
              key={row.id}
            >
              {row.getVisibleCells().map((cell, i) => (
                <td key={i} className="text-md h-12 px-5 py-3 align-middle table-cell">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile List View */}
      <div className="md:hidden flex flex-col gap-4">
        {table.getRowModel().rows.map((row, i) => (
          <div
            key={row.id}
            onClick={e => {
              const selection = window.getSelection();
              if (onRowClick && (!selection || selection.toString().length === 0)) {
                onRowClick(row as Row<DataTableType>);
              }
            }}
            className={classNames(
              'border border-gray-200 rounded-lg p-4 bg-white',
              onRowClick ? 'cursor-pointer hover:bg-gray-50' : '',
            )}
          >
            {row.getVisibleCells().map((cell, cellIndex) => {
              const header =
                typeof cell.column.columnDef.header === 'string' ? cell.column.columnDef.header : cell.column.id;
              const value = flexRender(cell.column.columnDef.cell, cell.getContext());

              return (
                <div key={cellIndex} className="flex  py-2 border-b last:border-b-0">
                  <div className="text-sm text-gray-600 font-medium mb-1 flex-1">{header}</div>
                  <div className="text-md text-gray-900 flex-1">{value}</div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
};

export default Table;
